/**
 * Awesome Aminos — motion primitives.
 *
 * No animation library. Everything here is IntersectionObserver, sticky
 * positioning and transforms, which is all the design actually needs and
 * avoids shipping ~50KB of GSAP for three effects.
 *
 * Exports nothing; registers behaviour on load:
 *   - [data-aa-reveal]  reveal-on-scroll
 *   - <aa-counter>      count-up numerals
 *   - <aa-timeline>     scroll-pinned horizontal timeline
 *
 * Every piece degrades to "final state, visible, no motion" when reduced
 * motion is requested or the API is unavailable.
 */

/**
 * Treat `?aa-static=1` exactly like reduced motion: everything renders in its
 * final state with no transitions. Used when capturing screenshots for visual
 * comparison against the design, so a half-played reveal can't be mistaken for
 * a layout bug.
 */
const isStatic = () => {
  try {
    return new URLSearchParams(location.search).get('aa-static') === '1';
  } catch {
    return false;
  }
};

const prefersReduced = () =>
  isStatic() || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------
 * Reveal on scroll
 * ---------------------------------------------------------------------- */

function initReveals() {
  // Without IO we never hide anything — content must not depend on JS.
  if (!('IntersectionObserver' in window)) return;

  // Nor when motion is unwanted: adding the class first would hide content
  // that nothing is then going to reveal.
  if (prefersReduced()) return;

  document.documentElement.classList.add('aa-motion-ready');

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target); // reveal once, never re-hide
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
  );

  /**
   * Already on screen at load? Reveal it without waiting for a scroll.
   *
   * The observer's negative bottom rootMargin is there so reveals fire when an
   * element is comfortably in view rather than the instant it clips the edge.
   * The side effect is that anything sitting in that bottom band at load — the
   * press bar, at most viewport heights — is visible to the user but outside
   * the observer's box, so it stays hidden until they happen to scroll.
   */
  const onScreenNow = (el) => {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  };

  const register = (el) => {
    if (el.dataset.aaRevealBound) return;
    el.dataset.aaRevealBound = '1';

    // data-aa-reveal="3" staggers by 3 steps of 70ms.
    const step = Number(el.dataset.aaReveal);
    if (Number.isFinite(step) && step > 0) {
      el.style.setProperty('--aa-reveal-delay', `${step * 70}ms`);
    }

    if (onScreenNow(el)) {
      // Deferred a beat so the hidden state paints first and the transition
      // actually runs, rather than snapping straight to the end state.
      setTimeout(() => el.classList.add('is-revealed'), 60);
    } else {
      io.observe(el);
    }
  };

  const scan = (root) => {
    if (!root || root.nodeType !== 1) return;
    if (root.matches('[data-aa-reveal]')) register(root);
    for (const el of root.querySelectorAll('[data-aa-reveal]')) register(el);
  };

  scan(document.body);

  /**
   * Sections can arrive after this runs, and the ones that do are the ones
   * that go missing: `.aa-motion-ready` hides every [data-aa-reveal] the
   * moment it is in the DOM, so a node nothing is observing stays at opacity 0
   * for good. That is what the theme editor does on every settings change, and
   * what `shopify theme dev` does on every hot reload — which is exactly the
   * "the section is there, then it isn't" symptom.
   */
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  if ('MutationObserver' in window) {
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) scan(node);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
}

/* -------------------------------------------------------------------------
 * <aa-counter to="87" suffix="%"> — counts up once on entry
 * ---------------------------------------------------------------------- */

class AACounter extends HTMLElement {
  connectedCallback() {
    // `lead`, not `prefix`: Element.prefix is the namespace prefix and is
    // read-only, so assigning to it throws out of connectedCallback.
    this.lead = '';
    const to = this.getAttribute('to');
    // Number(null) is 0, not NaN, so the missing attribute has to be checked
    // before the conversion or a counter with no `to` would count to zero.
    this.target = to === null ? NaN : Number(to);
    this.suffix = this.getAttribute('suffix') ?? '';
    this.duration = Number(this.getAttribute('duration')) || 1600;

    /**
     * No `to`? Read the final value out of the element's own text, keeping
     * whatever sits either side of the number. That is what lets a chart wrap
     * a value it already renders — "90%", "9 EAA'S" — without the section
     * having to split the figure from its unit in Liquid.
     */
    if (!Number.isFinite(this.target)) {
      const parsed = /^(\D*?)(-?[\d.]+)(.*)$/s.exec(this.textContent.trim());
      if (!parsed) return;
      this.lead = parsed[1];
      this.target = Number(parsed[2]);
      this.suffix = parsed[3];
      this.decimals = (parsed[2].split('.')[1] ?? '').length;
    }

    if (!Number.isFinite(this.target)) return;

    // Reduced motion, or no IO: show the real number immediately.
    if (prefersReduced() || !('IntersectionObserver' in window)) {
      this.render(this.target);
      return;
    }

    this.render(0);

    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.io.disconnect();
          this.run();
        }
      },
      { threshold: 0.6 }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  render(value) {
    const n = this.decimals ? value.toFixed(this.decimals) : Math.round(value);
    this.textContent = `${this.lead}${n}${this.suffix}`;
  }

  run() {
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / this.duration, 1);
      // easeOutCubic — fast start, settles onto the number
      const eased = 1 - Math.pow(1 - t, 3);
      this.render(this.target * eased);
      if (t < 1) this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }
}

/* -------------------------------------------------------------------------
 * <aa-reel> — scroll-driven step sequence
 *
 * Markup contract:
 *   <aa-reel>
 *     <div data-aa-reel-viewport>        sticky while pinned
 *       <div data-aa-reel-rail>          the steps' container
 *         <div data-aa-reel-step>…       one per step
 *         <span data-aa-reel-marker>     optional travelling marker
 *
 * Modes, because a six-step horizontal sequence pinned to a phone screen is
 * hostile, and because not every reel should be driven by scrolling at all:
 *
 *   manual             a reel with [data-aa-reel-prev]/[data-aa-reel-next]
 *                      controls. Never pins, never reads scroll — the arrows
 *                      are the only thing that moves it.
 *   pinned (>= 768px)  the host is tall and the viewport sticks. Progress comes
 *                      from the host's own rect; the marker runs left to right
 *                      and the active step grows and reveals its copy.
 *   rail   (< 768px)   nothing is pinned. The steps stack and the marker runs
 *                      top to bottom down the rail beside them, tracking
 *                      whichever step is at reading height.
 *   static             reduced motion, and manual reels on a phone: every step
 *                      legible, nothing pinned, nothing moving.
 *
 * The scroll-driven modes only read position — the page scrolls normally and
 * nothing is hijacked.
 * ---------------------------------------------------------------------- */

const clamp01 = (n) => Math.min(Math.max(n, 0), 1);

class AAReel extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-aa-reel-step]')];
    if (!this.steps.length) return;

    this.marker = this.querySelector('[data-aa-reel-marker]');
    this.markerImg = this.marker ? this.marker.querySelector('img') : null;
    this.rail = this.querySelector('[data-aa-reel-rail]') || this;
    this.activeIndex = -1;

    this.prevBtn = this.querySelector('[data-aa-reel-prev]');
    this.nextBtn = this.querySelector('[data-aa-reel-next]');
    this.manual = Boolean(this.prevBtn || this.nextBtn);
    // Bound once, not per mode change, or a resize would stack listeners.
    this.prevBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));
    if (this.manual) {
      this.rail.addEventListener('scroll', () => this.onRailScroll(), { passive: true });
    }

    this.mq = window.matchMedia('(min-width: 768px)');
    this.onChange = this.onChange.bind(this);
    this.mq.addEventListener('change', this.onChange);
    this.onChange();
  }

  disconnectedCallback() {
    this.teardown();
    this.mq?.removeEventListener('change', this.onChange);
  }

  onChange() {
    this.teardown();

    // Reduced motion gets the finished state: every step legible, nothing
    // pinned, nothing moving.
    if (prefersReduced()) {
      this.setAttribute('data-mode', 'static');
      this.setActive(this.manual ? 0 : this.steps.length - 1);
      this.syncControls();
      return;
    }

    // A manual reel has no scroll to read: set the first step and wait for a
    // click. No observer, no frame loop.
    if (this.manual) {
      this.mode = 'manual';
      this.setAttribute('data-mode', 'manual');
      this.setActive(Math.max(this.activeIndex, 0));
      this.syncControls();
      return;
    }

    this.mode = this.mq.matches ? 'pinned' : 'rail';
    this.setAttribute('data-mode', this.mode);

    /**
     * Drive from a rAF loop gated by an IntersectionObserver rather than from
     * scroll events. Scroll events are throttled or skipped outright in
     * embedded and background contexts, and a missed one leaves the marker
     * frozen mid-travel with no way to recover. A frame loop that runs only
     * while the section is on screen always reflects the true scroll position
     * and costs nothing while the section is away.
     */
    this.io = new IntersectionObserver(
      (entries) => (entries.some((e) => e.isIntersecting) ? this.loop() : this.pause()),
      { rootMargin: '15% 0px' }
    );
    this.io.observe(this);

    this.update();
  }

  teardown() {
    this.pause();
    cancelAnimationFrame(this.trackRaf);
    this.programmatic = false;
    this.io?.disconnect();
    this.io = null;
    this.mode = null;
    this.lastProgress = null;
    this.removeAttribute('data-mode');

    // The two modes move the marker on different axes, so a transform left
    // over from one would park it at the wrong end of the other.
    if (this.marker) this.marker.style.transform = '';
    if (this.markerImg) this.markerImg.style.transform = '';
  }

  loop() {
    if (this.raf) return;
    const frame = () => {
      this.update();
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  pause() {
    if (!this.raf) return;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  progress() {
    if (this.mode === 'pinned') {
      const rect = this.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return null;
      // 0 when the host's top hits the viewport top, 1 when its bottom does.
      return clamp01(-rect.top / scrollable);
    }

    const rect = this.rail.getBoundingClientRect();
    if (rect.height <= 0) return null;
    // The marker sits wherever the rail crosses reading height, so it stays
    // level with the step you are actually looking at.
    return clamp01((window.innerHeight * 0.55 - rect.top) / rect.height);
  }

  update() {
    const progress = this.progress();
    // The loop runs every frame while the section is on screen; skip the DOM
    // write when the position has not actually moved.
    if (progress === null || progress === this.lastProgress) return;
    this.lastProgress = progress;

    if (this.marker) {
      // The marker's own box spans the full length of its rail, so translating
      // it by a percentage of itself walks it end to end at any width. The
      // rotation goes on the image: rotating the rail would spin a full-length
      // strip and fling the marker clean off the track.
      this.marker.style.transform =
        this.mode === 'pinned'
          ? `translate3d(${progress * 100}%, 0, 0)`
          : `translate3d(0, ${progress * 100}%, 0)`;

      if (this.markerImg) {
        this.markerImg.style.transform = `rotate(${progress * 540}deg)`;
      }
    }

    this.setActive(
      Math.min(Math.floor(progress * this.steps.length), this.steps.length - 1)
    );
    this.style.setProperty('--aa-reel-progress', progress.toFixed(4));
  }

  go(delta) {
    const last = this.steps.length - 1;
    this.setActive(Math.min(Math.max(this.activeIndex + delta, 0), last));
    this.syncControls();
    this.trackActive();
  }

  /* On a phone the row is wider than the screen, so stepping has to bring the
     active step into view. */
  scrolls() {
    return this.rail.scrollWidth > this.rail.clientWidth + 4;
  }

  /**
   * Follow the active step for the length of its resize rather than scrolling
   * to where it is right now: it is still growing, and everything after it is
   * still sliding, so a single scrollTo would aim at a position that no longer
   * exists by the time it lands. Each frame nudges a fraction of the remaining
   * distance, which eases the travel and self-corrects as the layout settles.
   */
  trackActive() {
    const card = this.steps[this.activeIndex];
    if (!card || !this.scrolls()) return;

    cancelAnimationFrame(this.trackRaf);
    const until = performance.now() + 700;
    const pad = parseFloat(getComputedStyle(this.rail).paddingLeft) || 0;

    const follow = () => {
      this.programmatic = true;
      const delta = card.getBoundingClientRect().left
        - this.rail.getBoundingClientRect().left
        - pad;
      this.rail.scrollLeft += delta * 0.2;

      if (performance.now() < until) {
        this.trackRaf = requestAnimationFrame(follow);
      } else {
        // Released a beat later, so the tail of the scroll is not mistaken for
        // the reader swiping.
        setTimeout(() => { this.programmatic = false; }, 90);
      }
    };
    follow();
  }

  /* A swipe is the obvious gesture on a phone, so the selection follows the
     rail rather than only the arrows. */
  onRailScroll() {
    if (this.programmatic || this.mode !== 'manual') return;
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      const left = this.rail.getBoundingClientRect().left;
      let nearest = 0;
      let best = Infinity;
      this.steps.forEach((step, i) => {
        const d = Math.abs(step.getBoundingClientRect().left - left);
        if (d < best) { best = d; nearest = i; }
      });
      this.setActive(nearest);
      this.syncControls();
    }, 140);
  }

  /* The arrows stop at the ends rather than wrapping: a reel of six is short
     enough that looping back to the start reads as a glitch. */
  syncControls() {
    if (!this.manual) return;
    const manual = this.mode === 'manual';
    if (this.prevBtn) this.prevBtn.disabled = manual && this.activeIndex <= 0;
    if (this.nextBtn) {
      this.nextBtn.disabled = manual && this.activeIndex >= this.steps.length - 1;
    }
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.steps.forEach((step, i) => {
      step.classList.toggle('is-active', i === index);
      step.classList.toggle('is-passed', i < index);
    });
  }
}


/* -------------------------------------------------------------------------
 * Muted autoplay previews
 *
 * Videos marked [data-aa-autoplay] loop silently, but only while they are on
 * screen. Playing every clip on the page at once is what makes a wall of
 * autoplaying video janky and hot; an IntersectionObserver keeps exactly the
 * visible ones running.
 *
 * Under reduced motion nothing autoplays — the poster stays, and the clip is
 * still reachable through the lightbox.
 * ---------------------------------------------------------------------- */

function initAutoplay() {
  const videos = [...document.querySelectorAll('video[data-aa-autoplay]')];
  if (!videos.length) return;

  if (prefersReduced() || !('IntersectionObserver' in window)) {
    for (const v of videos) v.removeAttribute('autoplay');
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const v = entry.target;
        if (entry.isIntersecting) {
          // muted is required for autoplay to be allowed at all
          v.muted = true;
          const p = v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      }
    },
    { threshold: 0.25 }
  );

  for (const v of videos) io.observe(v);
}

/* -------------------------------------------------------------------------
 * Lightbox
 *
 * One <dialog> reused by every clip. Native showModal() gives focus trapping,
 * Esc-to-close and an inert background for free — all things a hand-rolled
 * div would have to reimplement badly.
 *
 * The preview is muted and loops; the lightbox copy plays with sound and
 * controls, which is the whole point of opening it.
 * ---------------------------------------------------------------------- */

function initLightbox() {
  const triggers = document.querySelectorAll('[data-aa-lightbox]');
  if (!triggers.length || !HTMLDialogElement.prototype.showModal) return;

  let dialog;

  /** Stop playback and detach the media. Safe to call repeatedly. */
  const release = () => {
    const v = dialog.querySelector('video');
    v.pause();
    v.removeAttribute('src');
    v.innerHTML = '';
    v.load();
  };

  const dismiss = () => {
    release();
    if (dialog.open) dialog.close();
  };

  const build = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'aa-lightbox';
    dialog.innerHTML =
      '<button class="aa-lightbox__close" type="button" aria-label="Close">&times;</button>' +
      '<video class="aa-lightbox__video" controls playsinline preload="metadata"></video>';
    document.body.appendChild(dialog);

    dialog.querySelector('.aa-lightbox__close').addEventListener('click', dismiss);

    // Click outside the video closes, matching what a backdrop click implies.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dismiss();
    });

    // Esc. `cancel` fires before the dialog actually closes.
    dialog.addEventListener('cancel', release);

    // Backstop only. The close event is queued as a task, and a throttled or
    // backgrounded document may never run it — which would leave a clip
    // playing audio behind a closed dialog. Every interactive path above
    // therefore releases the media itself rather than waiting for this.
    dialog.addEventListener('close', release);
  };

  const open = (source) => {
    if (!dialog) build();
    const v = dialog.querySelector('video');
    v.innerHTML = '';
    for (const s of source.querySelectorAll('source')) {
      v.appendChild(s.cloneNode());
    }
    const poster = source.getAttribute('poster');
    if (poster) v.setAttribute('poster', poster);
    v.load();
    dialog.showModal();
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  for (const trigger of triggers) {
    trigger.addEventListener('click', () => {
      const video = trigger.matches('video') ? trigger : trigger.querySelector('video');
      if (video) open(video);
    });
  }
}

/* -------------------------------------------------------------------------
 * Inline play
 *
 * The UGC clips run as a silent wall of loops. Clicking one turns it into the
 * clip you are actually watching: sound on, from the top, once through. Only
 * one at a time — clicking another hands over, and the one you left goes back
 * to being a silent loop like the rest of the row.
 *
 * No dialog. The clip plays where it sits, so the row never moves and the
 * other clips keep running around it.
 * ---------------------------------------------------------------------- */

function initInlinePlay() {
  const triggers = [...document.querySelectorAll('[data-aa-inline-play]')];
  if (!triggers.length) return;

  let active = null;

  const play = (video) => {
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  const toPreview = (trigger) => {
    trigger.classList.remove('is-playing');
    const video = trigger.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.currentTime = 0;
    // Under reduced motion nothing loops on its own, so leave it stopped.
    if (!prefersReduced()) play(video);
  };

  const toPlaying = (trigger) => {
    const video = trigger.querySelector('video');
    if (!video) return;
    trigger.classList.add('is-playing');
    // Unmuting is only allowed off the back of the click that got us here.
    video.muted = false;
    video.loop = false;
    video.currentTime = 0;
    play(video);
  };

  for (const trigger of triggers) {
    const video = trigger.querySelector('video');
    if (!video) continue;

    trigger.addEventListener('click', () => {
      if (active === trigger) {
        active = null;
        toPreview(trigger);
        return;
      }
      if (active) toPreview(active);
      active = trigger;
      toPlaying(trigger);
    });

    video.addEventListener('ended', () => {
      if (active === trigger) active = null;
      toPreview(trigger);
    });
  }
}

/* -------------------------------------------------------------------------
 * Accordions — <details data-aa-accordion> opens and closes on a slide
 * ---------------------------------------------------------------------- */

/**
 * <details> has no animatable open state that every browser agrees on yet, so
 * the height is animated here and the `open` attribute is only flipped at the
 * ends: set before an opening slide so the content is laid out to measure, and
 * cleared after a closing one so it stays visible on the way out.
 *
 * Delegated from the document, so panels that arrive later — a section reload
 * in the theme editor, a hot reload in dev — need no rebinding.
 *
 * With JavaScript off, or with motion turned down, nothing here runs and the
 * elements behave as the plain <details> they are.
 */
function initAccordions() {
  if (prefersReduced()) return;

  const DURATION = 260;
  const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  const bodyOf = (details) => details.querySelector('summary')?.nextElementSibling ?? null;

  const slide = (details, opening) => {
    const body = bodyOf(details);
    if (!body) {
      details.open = opening;
      return Promise.resolve();
    }

    details.aaAnim?.cancel();
    const run = (details.aaRun ?? 0) + 1;
    details.aaRun = run;

    const from = opening ? 0 : body.getBoundingClientRect().height;
    if (opening) details.open = true;
    const to = opening ? body.getBoundingClientRect().height : 0;

    body.style.overflow = 'hidden';
    const anim = body.animate(
      [
        { height: `${from}px`, opacity: opening ? 0 : 1 },
        { height: `${to}px`, opacity: opening ? 1 : 0 },
      ],
      { duration: DURATION, easing: EASE }
    );
    details.aaAnim = anim;

    /**
     * The end state is applied by whichever comes first — the animation
     * finishing, or a timer just past its duration. A panel closes by clearing
     * `open` at the end, so if the animation never reports back (a throttled
     * background tab is enough) the panel would stay open with its content
     * collapsed to nothing. The timer is what stops a dropped event from
     * leaving a panel in a state no further click can get it out of.
     *
     * The run token means a slide that has already been superseded does not
     * apply its end state over the top of the one that replaced it.
     */
    return new Promise((resolve) => {
      const settle = () => {
        // Superseded slides still resolve — a caller waiting on this one (the
        // group restoring a name it lifted) must not be left hanging.
        if (details.aaRun === run) {
          details.aaAnim = null;
          body.style.overflow = '';
          if (!opening) details.open = false;
        }
        resolve();
      };
      anim.onfinish = settle;
      setTimeout(settle, DURATION + 140);
    });
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;
    if (!details.hasAttribute('data-aa-accordion')) return;

    event.preventDefault();
    if (details.aaAnim) return;

    if (details.open) {
      slide(details, false);
      return;
    }

    /**
     * A named group is the browser's own one-at-a-time accordion, and it
     * closes the outgoing panel the instant this one opens — with no chance to
     * animate it. Lifting the name for the length of the slide takes the
     * outgoing panel out of the group so it can close on its own terms, and
     * putting it back leaves the no-script behaviour exactly as it was.
     */
    const group = details.getAttribute('name');
    if (group) {
      for (const other of document.querySelectorAll(`details[name="${CSS.escape(group)}"][open]`)) {
        if (other === details) continue;
        other.removeAttribute('name');
        slide(other, false).then(() => other.setAttribute('name', group));
      }
    }

    slide(details, true);
  });
}

/* ---------------------------------------------------------------------- */

customElements.define('aa-counter', AACounter);
customElements.define('aa-reel', AAReel);

function init() {
  initReveals();
  initAccordions();
  initAutoplay();
  initInlinePlay();
  initLightbox();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
